/**
 * LAS FECHAS Y LAS HORAS DEL PORTAL SON LAS DE CARACAS
 * ════════════════════════════════════════════════════
 *
 * Pedido de la usuaria el 2026-09-02: «necesito que las horas sean precisas y
 * correctas en todo el portal». Este módulo es el ÚNICO sitio del navegador
 * donde se pregunta qué día es hoy y donde se convierte un instante en texto,
 * igual que `formato.ts` es el único que formatea cifras (NORMA 2). Su gemelo
 * del servidor es `netlify/functions/_lib/fechas.ts`.
 *
 * DOS FALLOS DISTINTOS, los dos silenciosos:
 *
 * 1 · **`new Date().toISOString().slice(0, 10)` NO es «hoy»: es el día de UTC.**
 *     Venezuela va en UTC−4, así que **entre las 20:00 y la medianoche eso ya es
 *     MAÑANA**. Ocho pantallas lo usaban para el valor por defecto de un filtro
 *     de fecha, para el mes en curso y —lo peor— para la vigencia de una lista
 *     de precios, o sea que el dato guardado nacía con la fecha de mañana.
 *
 * 2 · **`d.getHours()` y `toLocaleString()` sin zona son la hora DEL EQUIPO.**
 *     Hoy aciertan porque todos los equipos están en Venezuela, y mienten el día
 *     que alguien abra el portal desde un portátil con otra zona o mal
 *     configurado. La hora del portal no es la del que mira: es la de Caracas.
 *
 * ⚠️ Nada de esto vale para una cadena que YA es una fecha sin hora
 * (`'2026-09-02'`, las columnas `date` de la base). Esas se parten por texto
 * (`fechaCortaVE`) y NUNCA se meten en un `new Date()`, que las lee como
 * medianoche UTC y las corre al día anterior al pintarlas.
 */

export const ZONA_VE = 'America/Caracas';

/** El día de hoy en Caracas, `YYYY-MM-DD`. `en-CA` formatea justo así. */
export const hoyISO = (): string =>
  new Date().toLocaleDateString('en-CA', { timeZone: ZONA_VE });

/** El mes en curso en Caracas, `YYYY-MM`. */
export const mesISO = (): string => hoyISO().slice(0, 7);

/** El día de hoy en Caracas sin guiones, `YYYYMMDD` — para nombres de archivo. */
export const hoyCompacto = (): string => hoyISO().replace(/-/g, '');

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * `aaaamm` (202607) → «Jul-26», la etiqueta corta de un mes. Es la que usa
 * Negociaciones en pantalla y en su Excel; vivía en la página y se sacó aquí
 * el 2026-09-07 para que el libro no tuviera que importar la página.
 */
export const ymTexto = (ym: number): string =>
  `${MESES_CORTOS[(ym % 100) - 1] ?? '??'}-${String(Math.floor(ym / 100)).slice(2)}`;

/** Las partes de un instante ISO, en la hora de Caracas. */
function partes(iso: string): Record<string, string> | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_VE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const p: Record<string, string> = {};
  for (const x of f) if (x.type !== 'literal') p[x.type] = x.value;
  /* `hour12: false` puede dar «24» a medianoche según el motor. */
  if (p.hour === '24') p.hour = '00';
  return p;
}

/** Un instante ISO → `dd/mm/aaaa hh:mm` en Caracas. Vacío si no es fecha. */
export function fechaHoraVE(iso: string | null | undefined, siFalta = ''): string {
  if (!iso) return siFalta;
  const p = partes(iso);
  if (!p) return siFalta;
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** Un instante ISO → `hh:mm` en Caracas. */
export function horaVE(iso: string | null | undefined, siFalta = ''): string {
  if (!iso) return siFalta;
  const p = partes(iso);
  return p ? `${p.hour}:${p.minute}` : siFalta;
}

/** Un instante ISO → `dd/mm/aaaa` en Caracas. */
export function fechaVE(iso: string | null | undefined, siFalta = ''): string {
  if (!iso) return siFalta;
  const p = partes(iso);
  return p ? `${p.day}/${p.month}/${p.year}` : siFalta;
}

/**
 * Una fecha SIN hora (`'2026-09-02'`, una columna `date`) → `dd/mm/aaaa`.
 * Se parte por texto a propósito: `new Date('2026-09-02')` es medianoche UTC y
 * pintarla en Caracas daría el 01/09.
 */
export const fechaCortaVE = (s: string | null | undefined, siFalta = '—'): string =>
  (s ? s.split('T')[0]!.split('-').reverse().join('/') : siFalta);

/** «Ahora» en Caracas como `dd/mm/aaaa hh:mm` — el sello de un documento. */
export const ahoraVE = (): string => fechaHoraVE(new Date().toISOString());

/** El valor de un `<input type="datetime-local">` con la hora de Caracas. */
export function ahoraLocalVE(): string {
  const p = partes(new Date().toISOString());
  if (!p) return '';
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
